/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnFiltersState, RowSelectionState, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { FC, Fragment, useState } from "react";
// import arrowDownIcon from "../../assets/arrow-down.svg";
import { Button, Spinner } from "src/components";
import BlockIcon from "src/assets/slash.svg";
import { cn } from "src/utils/utils";

interface TableProps {
  columns: any[];
  data: any[];
  onRowClick?: (row: any) => void;
  title?: string;
  subtitle?: string;
  isGlobalSearch?: boolean;
  searchPlaceholder?: string;
  showheader?: boolean;
  cta1Action?: any;
  cta2Action?: any;
  cta1?: string;
  cta2?: string;
  blockAction?: any;
  headClass?: string;
  isLoading?: boolean;
  hasPermissionAddDivision?: boolean;
  hasPermissionDeactivateDivision?: boolean;
  hasPermissionBlockDivision?: boolean;
  enableShowError?: boolean;
}

const SubTable: FC<TableProps> = ({
  columns,
  data,
  onRowClick,
  title,
  // subtitle,
  cta1Action,
  cta2Action,
  cta1,
  cta2,
  blockAction,
  showheader = true,
  headClass,
  isLoading,
  hasPermissionAddDivision,
  hasPermissionDeactivateDivision,
  hasPermissionBlockDivision,
  enableShowError = false,
}) => {
  const [columnFilters, setColumnsFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    state: {
      columnFilters,
      rowSelection,
    },
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (row) => row.counter_field_force_id,
    getCoreRowModel: getCoreRowModel(),
    onColumnFiltersChange: setColumnsFilters,
    onRowSelectionChange: setRowSelection,
  });

  return (
    <>
      <div className="bg-[#F8F8F8] w-full rounded-[0.652rem]">
        {title && (
          <section className="flex justify-between items-center px-4 py-3">
            <div>
              <h4 style={{ fontFamily: "Inter", fontSize: "14px", fontStyle: "normal", fontWeight: "600", lineHeight: "18px", color: " #000" }}>
                {title}
              </h4>
              {/* <p className="text-sm font-medium text-[#49484C] opacity-60">{subtitle}</p> */}
            </div>
            <div className="flex gap-2">
              {blockAction && hasPermissionBlockDivision && (
                <Button onClick={blockAction} className="border">
                  <img src={BlockIcon} alt="block" width={16} height={16} />
                </Button>
              )}
              {cta1 && hasPermissionDeactivateDivision && (
                <Button
                  style={{
                    color: "#303030",
                    borderRadius: "6px",
                    border: "1px solid #E3E3E3",
                    backgroundColor: "#F1F1F1",
                    boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.16)",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: "450",
                    lineHeight: "20px",
                  }}
                  onClick={() => cta1Action()}
                >
                  {cta1}
                </Button>
              )}
              {cta2 && hasPermissionAddDivision && (
                <Button
                  style={{
                    color: "#E3E3E3",
                    borderRadius: "6px",
                    backgroundColor: "#8051FF",
                    boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.16)",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: "450",
                    lineHeight: "20px",
                  }}
                  onClick={() => cta2Action()}
                >
                  {cta2}
                </Button>
              )}
            </div>
          </section>
        )}

        {isLoading && (
          <div className="flex justify-center w-full py-5">
            <Spinner />
          </div>
        )}

        {!isLoading && (
          <div className="overflow-auto pl-5 pr-5 pb-5">
            <div className="w-full border rounded-[0.625rem] overflow-hidden">
              <table className="w-full text-sm text-left text-[#49484C] font-medium table-auto">
                {showheader && (
                  <thead className={cn(`pt-4 w-full ${headClass ? headClass : ""}`)}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {/* serial no issue */}
                        <th
                          className="py-2 pl-2 text-left border border-b-0"
                          style={{
                            fontFamily: "Inter",
                            fontSize: "12px",
                            fontStyle: "normal",
                            fontWeight: "600",
                            lineHeight: "16px",
                            width: "40px",
                          }}
                        >
                          S.No
                        </th>

                        {headerGroup.headers.map((header) => (
                          <th
                            className="py-2 pl-2 text-left border border-b-0"
                            key={header.id}
                            style={{
                              fontFamily: "Inter",
                              fontSize: "12px",
                              fontStyle: "normal",
                              fontWeight: "600",
                              lineHeight: "16px",
                            }}
                          >
                            <div className="flex overflow-hidden gap-2 items-center max-w-full">
                              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                              {/* {header.column.getCanSort() && (
                              <button onClick={header.column.getToggleSortingHandler()}>
                                <img src={arrowDownIcon} alt="sorting" />
                              </button>
                            )} */}
                              {{ asc: " 🔼", desc: " 🔽" }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                )}
                <tbody className="p-0 bg-white">
                  {/* Sorts rows with a numeric id ascending; rows without one (e.g. line items with no
                      persisted id yet) keep their original insertion order instead of reshuffling on
                      every re-render — Array.prototype.sort is stable, so returning 0 here preserves it. */}
                  {table.getRowModel().rows?.sort((a, b) => {
                    const aId = a?.original?.id;
                    const bId = b?.original?.id;
                    if (aId === undefined && bId === undefined) return 0;
                    if (aId === undefined) return 1;
                    if (bId === undefined) return -1;
                    return aId - bId;
                  })?.map((row, index) => {
                    return (
                      <Fragment key={row.id}>
                        <tr
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onRowClick) onRowClick(row);
                          }}
                          style={{
                            backgroundColor: !row.original?.mrp_valid && enableShowError
                              ? "#FFFFE0"
                              : enableShowError && (
                                (Number(row.original?.discount_perc) === 0 &&
                                  Number(row.original?.discount_amt) === 0 &&
                                  Number(row.original?.free_qty) === 0) ||
                                (Number(row.original?.quantity) === 0 &&
                                  Number(row.original?.free_qty) === 0)
                              )
                                ? "#FFDEE1"
                                : ""
                          }}
                        >
                          {/* serial no issue */}
                          <td
                            className="overflow-hidden py-2 pl-2 whitespace-nowrap border border-b-0"
                            style={{
                              fontFamily: "Inter",
                              fontSize: "12px",
                              fontStyle: "normal",
                              fontWeight: "450",
                              lineHeight: "16px",
                              width: "40px",
                            }}
                          >
                            {index + 1}
                          </td>
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="overflow-hidden py-2 pl-2 whitespace-nowrap border border-b-0"
                              style={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                fontStyle: "normal",
                                fontWeight: "450",
                                lineHeight: "16px",
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  {table.getFooterGroups().map((footerGroup) => (
                    <tr key={footerGroup.id}>
                      {footerGroup.headers.map((header) => (
                        <th key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}</th>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SubTable;
